import { Badge, Skeleton } from '@personachat/ui';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../api/client';
import { lire } from '../../api/call';
import { ErreurChargement, Section, StatutBadge, TypeBadge } from '../../components/bits';
import { Drawer } from '../../components/Drawer';
import { TabPanel, Tabs, type Onglet } from '../../components/Tabs';
import { formatNombre } from '../../lib/format';
import { AuditTimeline } from '../audit/AuditTimeline';
import { ExportRgpd, MemoiresEleve } from '../privacy/Confidentialite';

import { ActivityTab } from './ActivityTab';
import { CoachesTab } from './CoachesTab';
import { CreditsTab } from './CreditsTab';
import { AccesBadge } from './AccesSection';
import { ProfileTab } from './ProfileTab';

type IdOnglet = 'profil' | 'credits' | 'coachs' | 'activite' | 'journal' | 'memoire';

const ONGLETS: IdOnglet[] = ['profil', 'credits', 'coachs', 'activite', 'journal', 'memoire'];

export function useEleve(id: string) {
  return useQuery({
    queryKey: ['eleve', id],
    queryFn: async () => {
      const r = await lire(api.GET('/api/admin/users/{id}', { params: { path: { id } } }));
      return { ...r.user, acces: r.access };
    },
  });
}

/**
 * Fiche élève en panneau latéral (cahier des charges §7.2), un onglet par
 * sujet ; l'onglet ouvert est dans l'URL (?onglet=credits).
 */
export function StudentDrawer() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const onglet = (ONGLETS.includes(params.get('onglet') as IdOnglet) ? params.get('onglet') : 'profil') as IdOnglet;
  const eleve = useEleve(id);

  const fermer = () => {
    const suivants = new URLSearchParams(location.search);
    suivants.delete('onglet');
    const q = suivants.toString();
    navigate(`/eleves${q ? `?${q}` : ''}`);
  };

  const changerOnglet = (o: IdOnglet) => {
    setParams(
      (actuels) => {
        const s = new URLSearchParams(actuels);
        if (o === 'profil') s.delete('onglet');
        else s.set('onglet', o);
        return s;
      },
      { replace: true }
    );
  };

  const u = eleve.data;
  const onglets: Onglet<IdOnglet>[] = [
    { id: 'profil', label: 'Profil' },
    { id: 'credits', label: 'Crédits' },
    { id: 'coachs', label: 'Coachs', count: u?._count.unlockedPersonalities ?? null },
    { id: 'activite', label: 'Activité' },
    { id: 'journal', label: 'Journal' },
    { id: 'memoire', label: 'Mémoire, RGPD' },
  ];

  const libelles: Record<IdOnglet, string> = {
    profil: 'Profil',
    credits: 'Crédits',
    coachs: 'Coachs',
    activite: 'Activité',
    journal: 'Journal',
    memoire: 'Mémoire, RGPD',
  };

  return (
    <Drawer
      onClose={fermer}
      label={u ? `Fiche de ${u.email}` : 'Fiche élève'}
      title={u ? (u.fullName ?? u.username ?? u.email) : <Skeleton width={220} height={22} />}
      subtitle={u ? u.email : undefined}
      badges={
        u ? (
          <>
            <TypeBadge type={u.userType} isAdmin={u.isAdmin} />
            <StatutBadge isActive={u.isActive} deletedAt={u.deletedAt} />
            {!u.deletedAt && !u.isAdmin && <AccesBadge acces={u.acces} />}
            {!u.isEmailVerified && <Badge tone="danger">E-mail non vérifié</Badge>}
            <Badge tone="gold">{u.creditWallet?.isUnlimited ? 'Crédits illimités' : `${formatNombre(u.creditWallet?.balance ?? 0)} crédits`}</Badge>
          </>
        ) : undefined
      }
    >
      {eleve.isError ? (
        <div style={{ paddingTop: 16 }}>
          <ErreurChargement erreur={eleve.error} onReessayer={() => void eleve.refetch()} />
        </div>
      ) : !u ? (
        <div className="stack" style={{ paddingTop: 16 }}>
          <Skeleton height={36} />
          <Skeleton height={180} />
        </div>
      ) : (
        <>
          <Tabs label="Sections de la fiche" onglets={onglets} actif={onglet} onChange={changerOnglet} />
          <TabPanel label={libelles[onglet]}>
            {onglet === 'profil' && <ProfileTab eleve={u} />}
            {onglet === 'credits' && <CreditsTab eleve={u} />}
            {onglet === 'coachs' && <CoachesTab eleve={u} />}
            {onglet === 'activite' && <ActivityTab eleve={u} />}
            {onglet === 'journal' && <AuditTimeline targetId={u.id} />}
            {onglet === 'memoire' && (
              <>
                <div style={{ marginBottom: 24 }}>
                  <MemoiresEleve key={u.id} eleveId={u.id} />
                </div>
                <Section title="Données personnelles (RGPD)">
                  <p className="texte-aide" style={{ marginBottom: 10 }}>
                    Accès ou portabilité : exportez toutes ses données. Rectification ou effacement d’un souvenir de l’IA : corrigez la
                    mémoire ci-dessus. Effacement complet : un super admin supprime le compte depuis l’onglet Profil.
                  </p>
                  <ExportRgpd eleveId={u.id} email={u.email} />
                </Section>
              </>
            )}
          </TabPanel>
        </>
      )}
    </Drawer>
  );
}
